import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config/api';

interface MeasurementSet {
  id: string;
  name: string;
  updatedAt: string;
}

interface MeasurementSetSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  onSelect: (measurementSetId: string) => Promise<void>;
  currentMeasurementSetId?: string;
}

const MeasurementSetSelector: React.FC<MeasurementSetSelectorProps> = ({
  isOpen,
  onClose,
  customerId,
  onSelect,
  currentMeasurementSetId,
}) => {
  const [measurementSets, setMeasurementSets] = useState<MeasurementSet[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (isOpen && customerId) {
      fetchMeasurementSets();
    }
  }, [isOpen, customerId]);
  
  const fetchMeasurementSets = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Fetch measurement sets for customer
      // Note: This endpoint may need to be created or adjusted based on your API
      const response = await fetch(`${API_BASE}/api/measurements-photos/customers/${customerId}/measurements`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch measurement sets');
      }
      
      const data = await response.json();
      // Transform to measurement sets format
      const sets: MeasurementSet[] = (Array.isArray(data) ? data : data.measurements || [])
        .filter((m: any) => m.id != null && m.id !== undefined) // Filter out null/undefined IDs
        .map((m: any) => ({
          id: String(m.id),
          name: m.name || `Measurement Set v${m.version || '1'}`,
          updatedAt: m.updated_at || m.created_at,
        }));
      
      setMeasurementSets(sets);
    } catch (_err) {
      setError(err instanceof Error ? err.message : 'Failed to load measurement sets');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelect = async (measurementSetId: string) => {
    // Validate measurementSetId before sending
    if (!measurementSetId || measurementSetId === 'null' || measurementSetId === 'undefined') {
      console.error('Invalid measurement set ID:', measurementSetId);
      setError('Please select a valid measurement set');
      return;
    }
    
    setError(null); // Clear any previous errors
    try {
      await onSelect(measurementSetId);
      onClose();
    } catch (_err) {
      console.error('Failed to attach measurement set:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to attach measurement set';
      setError(errorMessage);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Attach Measurement Set</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading measurement sets...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600">{error}</p>
              <button
                onClick={fetchMeasurementSets}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          ) : measurementSets.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No measurement sets available</p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {measurementSets.map((set) => (
                <button
                  key={set.id}
                  onClick={() => handleSelect(set.id)}
                  className={`w-full p-3 border rounded-lg text-left hover:bg-gray-50 transition-colors ${
                    currentMeasurementSetId === set.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="font-medium text-gray-900">{set.name}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Updated: {new Date(set.updatedAt).toLocaleDateString()}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeasurementSetSelector;

